/**
 * Leer un archivo de intercambio y convertirlo en un cuestionario, o en un informe de errores.
 *
 * **La regla que evita el peor fallo**: la validez de cada pregunta la decide el módulo de su
 * tipo, exactamente el mismo que usa el editor para decir si un cuestionario se puede jugar. No
 * unas reglas equivalentes: **las mismas**. Si fueran dos implementaciones divergirían en la
 * primera regla que cambiara, y el síntoma sería un cuestionario que se importa sin quejas y no
 * se puede jugar, descubierto delante de la clase.
 */
import { isQuestionTypeId } from '../question-types/contract.js';
import { findDangerousMarkup } from './dangerous-markup.js';
import { defaultQuestionTypeId, questionTypeOf } from '../question-types/registry.js';
import { exchangeFileSchema, MAX_FILE_BYTES, MAX_QUESTIONS_PER_FILE, SUPPORTED_FORMAT_VERSION, } from './schema.js';
/** Qué motivo corresponde a cada clase de marcado peligroso. */
const DANGEROUS_REASON = {
    tag: 'dangerousTag',
    eventAttribute: 'dangerousEventAttribute',
    url: 'dangerousUrl',
};
/**
 * Analiza el texto pegado o el archivo arrastrado.
 *
 * Distingue **«esto no es un archivo válido»** de **«este archivo tiene errores»**, porque no se
 * arreglan igual: lo primero se soluciona volviendo a copiar, lo segundo editando el contenido.
 */
export function parseExchangeFile(text) {
    if (text.length > MAX_FILE_BYTES) {
        return {
            ok: false,
            errors: [{ reason: 'tooLarge', detail: String(MAX_FILE_BYTES) }],
        };
    }
    let parsed;
    try {
        // Se tolera lo que un copiar y pegar corriente añade alrededor.
        parsed = JSON.parse(text.trim());
    }
    catch {
        return { ok: false, errors: [{ reason: 'notJson' }] };
    }
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        return { ok: false, errors: [{ reason: 'notAnObject' }] };
    }
    // La versión se comprueba ANTES que la forma: un archivo de otra versión puede tener una
    // estructura distinta, y decir «falta el campo X» de un formato que no es el nuestro sería
    // confuso.
    const version = parsed['clica_format'];
    if (version === undefined) {
        return { ok: false, errors: [{ reason: 'missingFormatVersion' }] };
    }
    if (version !== SUPPORTED_FORMAT_VERSION) {
        return {
            ok: false,
            errors: [
                {
                    reason: 'unsupportedFormatVersion',
                    // La versión puede ser cualquier cosa: el archivo lo escribe quien sea. Se serializa
                    // en lugar de convertirla a texto sin más, que con un objeto daría «[object Object]».
                    detail: typeof version === 'number' ? String(version) : JSON.stringify(version),
                },
            ],
        };
    }
    const file = exchangeFileSchema.safeParse(parsed);
    if (!file.success) {
        return {
            ok: false,
            errors: file.error.issues.map((issue) => ({
                reason: 'malformedFile',
                field: issue.path.join('.'),
                detail: issue.message,
            })),
        };
    }
    return validateContent(file.data);
}
/** Comprueba el contenido con las mismas reglas que deciden si se puede jugar. */
function validateContent(file) {
    const { quiz } = file;
    const errors = [];
    if (quiz.questions.length === 0) {
        return { ok: false, errors: [{ reason: 'noQuestions' }] };
    }
    if (quiz.questions.length > MAX_QUESTIONS_PER_FILE) {
        return {
            ok: false,
            errors: [{ reason: 'tooManyQuestions', detail: String(MAX_QUESTIONS_PER_FILE) }],
        };
    }
    const questions = [];
    quiz.questions.forEach((question, index) => {
        // Numerada como la ve el profesor, empezando en uno.
        const questionNumber = index + 1;
        const type = question.type ?? defaultQuestionTypeId();
        if (!isQuestionTypeId(type)) {
            errors.push({ reason: 'unknownQuestionType', questionNumber, field: 'type', detail: type });
            return;
        }
        const options = question.options.map((option) => ({
            text_md: option.text,
            is_correct: option.correct ?? false,
        }));
        // RF-012: el marcado activo se rechaza al entrar, no solo al mostrarse. Se revisan todos los
        // textos con formato de la pregunta, incluida la pista, que también se renderiza.
        const dangerous = [
            { field: 'prompt', text: question.prompt },
            { field: 'hint', text: question.hint ?? '' },
            ...options.map((option, position) => ({
                field: `options.${position + 1}`,
                text: option.text_md,
            })),
        ]
            .map((candidate) => ({ ...candidate, kind: findDangerousMarkup(candidate.text) }))
            .find((candidate) => candidate.kind !== null);
        if (dangerous !== undefined && dangerous.kind !== null) {
            // Cada clase tiene su propio motivo, y por tanto su propia frase traducida: meter «tag» o
            // «url» como dato de una frase genérica pondría una palabra inglesa en la pantalla del
            // profesor (principio V).
            errors.push({
                reason: DANGEROUS_REASON[dangerous.kind],
                questionNumber,
                field: dangerous.field,
            });
            return;
        }
        // LA LÍNEA QUE IMPORTA: pregunta al módulo del tipo, con la forma que ese módulo espera.
        const problems = questionTypeOf(type).validateRaw({
            prompt_md: question.prompt,
            options: options.map((option, position) => ({
                position,
                text_md: option.text_md,
                is_correct: option.is_correct,
            })),
        });
        for (const problem of problems) {
            errors.push({
                reason: problem.reason,
                questionNumber,
                field: fieldFor(problem.reason),
                ...(problem.optionPosition === undefined
                    ? {}
                    : { detail: String(problem.optionPosition + 1) }),
            });
        }
        if (problems.length > 0)
            return;
        questions.push({
            type,
            prompt_md: question.prompt,
            hint_md: question.hint ?? null,
            time_limit_s: question.time_limit_s ?? null,
            hint_after_s: question.hint_after_s ?? null,
            shuffle_options: question.shuffle_options ?? null,
            points_multiplier: question.points_multiplier ?? null,
            options,
        });
    });
    if (errors.length > 0)
        return { ok: false, errors };
    return {
        ok: true,
        draft: {
            title: quiz.title,
            description: quiz.description ?? null,
            settings: quiz.settings ?? {},
            questions,
        },
    };
}
/** El campo del archivo al que corresponde cada motivo, para que se pueda buscar. */
function fieldFor(reason) {
    switch (reason) {
        case 'emptyPrompt':
        case 'promptTooLong':
            return 'prompt';
        case 'tooFewOptions':
        case 'tooManyOptions':
        case 'emptyOption':
        case 'optionTooLong':
            return 'options';
        case 'noCorrectOption':
        case 'severalCorrectOptions':
            return 'correct';
    }
}
//# sourceMappingURL=import.js.map